import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Car, Gauge, Calendar, DollarSign, Wrench, Clock, Plus,
  CheckCircle, AlertTriangle, AlertCircle, ChevronRight,
  Droplet, Circle, Octagon, Wind, Battery, Fan, FileText, Shield,
  ShieldCheck, Camera, Image, Sparkles
} from 'lucide-react';
import { getFileUrl } from '../../../utils/fileStorage';

// Icon mapping
const ICON_MAP = {
  'droplet': Droplet,
  'circle': Circle,
  'octagon': Octagon,
  'wind': Wind,
  'battery': Battery,
  'fan': Fan,
  'file-text': FileText,
  'shield': Shield,
  'shield-check': ShieldCheck,
  'shield-plus': Shield,
  'file-check': FileText,
  'wrench': Wrench,
};

// Health color mapping
const HEALTH_COLORS = {
  green: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    iconBg: 'bg-green-100',
    icon: 'text-green-600',
    title: 'text-green-800',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    icon: 'text-amber-600',
    title: 'text-amber-800',
  },
  red: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    icon: 'text-red-600',
    title: 'text-red-800',
  },
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    iconBg: 'bg-blue-100',
    icon: 'text-blue-600',
    title: 'text-blue-800',
  },
};

// Health status icons
const HEALTH_ICONS = {
  healthy: CheckCircle,
  ok: CheckCircle,
  due_soon: Clock,
  overdue: AlertCircle,
  setup_needed: Wrench,
  not_set: Wrench,
};

// Status configurations for items
const STATUS_CONFIG = {
  overdue: { label: 'Overdue', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', icon: AlertCircle },
  due_soon: { label: 'Due Soon', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
  ok: { label: 'On Track', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle },
  not_set: { label: 'Ready to Set Up', bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', icon: Clock },
};

const OverviewTab = ({ 
  healthData, 
  onLogService, 
  onAddReceipt,
  onItemClick,
  onUploadPhoto,
  theme 
}) => {
  const vehicle = healthData?.vehicle;
  const healthColor = HEALTH_COLORS[healthData?.health_color] || HEALTH_COLORS.blue;
  const HealthIcon = HEALTH_ICONS[healthData?.health_status] || Wrench;
  const nextActions = healthData?.next_actions || [];
  const hasActions = healthData?.has_actions;
  
  return (
    <div className="space-y-4">
      {/* Vehicle Info Card with Photo */}
      <Card className="overflow-hidden" style={{ backgroundColor: theme.surface }}>
        <div className="flex">
          {/* Vehicle Photo */}
          <div 
            className="w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0 flex items-center justify-center relative group cursor-pointer"
            style={{ backgroundColor: theme.light }}
            onClick={onUploadPhoto}
            data-testid="vehicle-photo-area"
          >
            {vehicle?.photo ? (
              <>
                <img 
                  src={getFileUrl(vehicle.photo)} 
                  alt={vehicle.nickname || vehicle.name}
                  className="w-full h-full object-cover"
                />
                {/* Hover overlay for changing photo */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="text-white text-center">
                    <Camera className="h-6 w-6 mx-auto mb-1" />
                    <span className="text-xs">Change Photo</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <Car className="h-12 w-12 mx-auto mb-1" style={{ color: theme.icon }} />
                <div className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-slate-600 transition-colors">
                  <Camera className="h-3 w-3" />
                  <span>Add Photo</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Vehicle Details */}
          <div className="flex-1 p-4">
            <h2 className="text-xl font-bold text-slate-800">
              {vehicle?.nickname || vehicle?.name}
            </h2>
            {vehicle?.nickname && (
              <p className="text-sm text-slate-500">{vehicle.name}</p>
            )}
            
            <div className="mt-3 space-y-1">
              {vehicle?.current_mileage > 0 && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Gauge className="h-4 w-4" />
                  <span>{vehicle.current_mileage.toLocaleString()} miles</span>
                </div>
              )}
              {vehicle?.vin && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>VIN: {vehicle.vin}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Vehicle Health Card */}
      <Card 
        className={`border-2 ${healthColor.border}`}
        style={{ backgroundColor: healthColor.bg.replace('bg-', '') }}
        data-testid="vehicle-health-card"
      >
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${healthColor.iconBg}`}>
              <HealthIcon className={`h-7 w-7 ${healthColor.icon}`} />
            </div>
            <div className="flex-1">
              <h2 className={`text-2xl font-bold ${healthColor.title}`}>
                {healthData?.health_title || 'Loading...'}
              </h2>
              <p className="text-slate-600 text-sm mt-0.5">
                {healthData?.health_subtitle || ''}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-200/50">
            <Button
              size="sm"
              onClick={() => onLogService()}
              style={{ backgroundColor: theme.icon }}
              className="hover:opacity-90"
              data-testid="quick-log-service-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Log Service
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onAddReceipt}
              data-testid="quick-add-receipt-btn"
            >
              <DollarSign className="h-4 w-4 mr-1" />
              Add Receipt
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Next Actions Section */}
      <Card style={{ backgroundColor: theme.surface }} data-testid="next-actions-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            {hasActions ? (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Next Actions
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 text-green-500" />
                All Clear
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {hasActions ? (
            <div className="space-y-2">
              {nextActions.map((action, index) => {
                const statusConfig = STATUS_CONFIG[action.status] || STATUS_CONFIG.not_set;
                const ActionIcon = ICON_MAP[action.icon] || Wrench;
                
                return (
                  <div
                    key={`${action.type}-${index}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${statusConfig.border}`}
                    onClick={() => onItemClick(action)}
                    data-testid={`next-action-${action.type}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${statusConfig.bg}`}>
                      <ActionIcon className={`h-5 w-5 ${statusConfig.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{action.name}</span>
                        <Badge className={`${statusConfig.bg} ${statusConfig.text} text-xs`}>
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <p className={`text-sm ${action.status === 'overdue' ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                        {action.urgency_text || 'Tap to view details'}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-2" />
              <p className="font-medium text-slate-700">Looking Good!</p>
              <p className="text-sm text-slate-500">Nothing needs your attention right now</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Maintenance Status */}
        <Card style={{ backgroundColor: theme.surface }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Wrench className="h-4 w-4" style={{ color: theme.icon }} />
              <span className="text-sm font-medium text-slate-600">Maintenance</span>
            </div>
            <div className="space-y-1">
              {healthData?.maintenance_stats?.overdue > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-sm text-red-600">{healthData.maintenance_stats.overdue} overdue</span>
                </div>
              )}
              {healthData?.maintenance_stats?.due_soon > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm text-amber-600">{healthData.maintenance_stats.due_soon} due soon</span>
                </div>
              )}
              {healthData?.maintenance_stats?.ok > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm text-green-600">{healthData.maintenance_stats.ok} good</span>
                </div>
              )}
              {healthData?.maintenance_stats?.not_set > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-slate-400" />
                  <span className="text-sm text-slate-500">{healthData.maintenance_stats.not_set} ready to set up</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Protection Status */}
        <Card style={{ backgroundColor: theme.surface }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4" style={{ color: theme.icon }} />
              <span className="text-sm font-medium text-slate-600">Protection</span>
            </div>
            <div className="space-y-1">
              {healthData?.protection_stats?.overdue > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-sm text-red-600">{healthData.protection_stats.overdue} expired</span>
                </div>
              )}
              {healthData?.protection_stats?.due_soon > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm text-amber-600">{healthData.protection_stats.due_soon} expiring</span>
                </div>
              )}
              {healthData?.protection_stats?.ok > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm text-green-600">{healthData.protection_stats.ok} active</span>
                </div>
              )}
              {(!healthData?.protection_items || healthData.protection_items.length === 0) && (
                <p className="text-sm text-slate-400">Ready to add protection</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Spending Summary */}
      {healthData?.spending && (
        <Card style={{ backgroundColor: theme.surface }} data-testid="spending-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <DollarSign className="h-5 w-5" style={{ color: theme.icon }} />
              Spending Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-4 gap-2">
              {[
                { label: '30 Days', value: healthData.spending.last_30_days },
                { label: '90 Days', value: healthData.spending.last_90_days },
                { label: '1 Year', value: healthData.spending.last_year },
                { label: 'Lifetime', value: healthData.spending.lifetime },
              ].map(({ label, value }) => (
                <div key={label} className="text-center p-2 rounded-lg" style={{ backgroundColor: theme.light }}>
                  <p className="text-lg font-bold" style={{ color: theme.dark }}>
                    ${value?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OverviewTab;
